import { useCallback, useEffect, useRef } from 'react';
import useStaticCallback from 'hooks/useStaticCallback';

type Task = (done: () => void) => void;
type StartPollilng = () => void;
type CancelPolling = () => void;

export interface UsePollingConfig {
  intervalMillis: number;
}

export type UsePollingResult = [StartPollilng, CancelPolling];

/**
 * A hook that polls for a task on an interval
 */
export default function usePolling(
  task: Task,
  { intervalMillis }: UsePollingConfig,
): UsePollingResult {
  const taskId = useRef<number | null>(null);
  const staticTask = useStaticCallback(task);

  const cancel = useCallback(() => {
    if (taskId.current) {
      window.clearInterval(taskId.current);
      taskId.current = null;
    }
  }, []);

  const poll = useCallback(() => {
    // safeguard to prevent overlapping runs of the same task.  if poll was
    // called while the polling job was running, cancel the current job to
    // restart it
    cancel();

    // function passed to task.  can be called by component to cancel the
    // polling job
    let isDone = false;
    const done = () => {
      isDone = true;
    };

    taskId.current = window.setTimeout(() => {
      staticTask(done);

      if (!isDone) {
        poll();
      }
    }, intervalMillis);
  }, [cancel, intervalMillis, staticTask]);

  useEffect(
    () => () => {
      cancel();
    },
    [cancel],
  );

  return [poll, cancel];
}
